import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  buildEchoMeasurements,
  emptyEchoMeasurementValues,
  getEchoMeasurementFields,
  PVL_SEVERITY_OPTIONS,
  YES_NO_OPTIONS,
} from "@/lib/echoMeasurements";
import { toast } from "sonner";

const fieldLabelClass = "text-xs text-black";
const fieldInputClass = "border-[var(--xl-line2)] bg-[var(--xl-surf)] text-black placeholder:text-black";

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const EchoMeasurementForm = ({
  modality,
  submitting = false,
  onSubmit,
  values: valuesProp,
  onValuesChange,
  showSubmit = true,
}) => {
  const fields = getEchoMeasurementFields(modality);
  const [internalValues, setInternalValues] = useState(() => emptyEchoMeasurementValues(modality));
  const isControlled = valuesProp !== undefined;
  const values = isControlled ? valuesProp : internalValues;

  useEffect(() => {
    if (isControlled) return;
    setInternalValues(emptyEchoMeasurementValues(modality));
  }, [modality, isControlled]);

  const setValues = (updater) => {
    const next = typeof updater === "function" ? updater(values) : updater;
    if (!isControlled) setInternalValues(next);
    onValuesChange?.(next);
  };

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const { measurements, missing } = buildEchoMeasurements(modality, values);
    if (missing.length > 0) {
      toast.error(
        missing.length === 1
          ? `Enter ${missing[0]}`
          : `Enter: ${missing.join(", ")}`,
      );
      return;
    }
    await onSubmit?.({ study_date: todayIsoDate(), measurements });
  };

  if (!fields.length) return null;

  const Wrapper = showSubmit ? "form" : "div";

  return (
    <Wrapper
      className="space-y-4"
      {...(showSubmit ? { onSubmit: handleSubmit } : {})}
      data-testid="echo-measurement-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {fields.map((field) => {
          const presentKey = `${field.name}__present`;
          const present = values[presentKey] ?? "";

          if (field.type === "yesno") {
            return (
              <fieldset key={field.name} className="space-y-2 min-w-0">
                <legend className={fieldLabelClass}>{field.name}</legend>
                <RadioGroup
                  value={values[field.name] || undefined}
                  onValueChange={(v) => handleChange(field.name, v)}
                  className="flex flex-wrap gap-4"
                  aria-label={field.name}
                >
                  {YES_NO_OPTIONS.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-2 text-sm text-black cursor-pointer"
                    >
                      <RadioGroupItem value={option} />
                      {option}
                    </label>
                  ))}
                </RadioGroup>
              </fieldset>
            );
          }

          if (field.type === "pvl") {
            return (
              <fieldset key={field.name} className="space-y-2 min-w-0">
                <legend className={fieldLabelClass}>{field.name}</legend>
                <RadioGroup
                  value={present || undefined}
                  onValueChange={(v) => {
                    setValues((prev) => ({
                      ...prev,
                      [presentKey]: v,
                      [field.name]: v === "No" ? "" : prev[field.name],
                    }));
                  }}
                  className="flex flex-wrap gap-4"
                  aria-label={`${field.name} present`}
                >
                  {YES_NO_OPTIONS.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-2 text-sm text-black cursor-pointer"
                    >
                      <RadioGroupItem value={option} />
                      {option}
                    </label>
                  ))}
                </RadioGroup>
                {present === "Yes" && (
                  <RadioGroup
                    value={values[field.name] || undefined}
                    onValueChange={(v) => handleChange(field.name, v)}
                    className="flex flex-wrap gap-4 pt-1"
                    aria-label={`${field.name} severity`}
                  >
                    {PVL_SEVERITY_OPTIONS.map((option) => (
                      <label
                        key={option}
                        className="flex items-center gap-2 text-sm text-black cursor-pointer"
                      >
                        <RadioGroupItem value={option} />
                        {option}
                      </label>
                    ))}
                  </RadioGroup>
                )}
              </fieldset>
            );
          }

          return (
            <div key={field.name} className="space-y-1.5 min-w-0">
              <Label htmlFor={`echo-field-${field.name}`} className={fieldLabelClass}>
                {field.name}
                {field.unit ? ` (${field.unit})` : ""}
              </Label>
              <Input
                id={`echo-field-${field.name}`}
                type="number"
                inputMode="decimal"
                step="any"
                value={values[field.name] ?? ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                className={`h-10 w-full ${fieldInputClass}`}
                data-testid={`echo-field-${field.name}`}
              />
            </div>
          );
        })}
      </div>

      {showSubmit ? (
        <div className="flex justify-end pt-1">
          <Button
            type="submit"
            size="sm"
            className="gap-2 rounded-full w-full sm:w-auto"
            disabled={submitting}
            data-testid="echo-form-save-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {submitting ? "Saving…" : "Save study"}
          </Button>
        </div>
      ) : null}
    </Wrapper>
  );
};

export default EchoMeasurementForm;
